<template>
  <div class="presentation-upload">
    <div class="upload-header">
      <h6 class="mb-0">Upload Presentation</h6>
    </div>

    <div class="upload-content">
      <!-- File Upload Area -->
      <div
        class="upload-zone"
        :class="{ 'drag-over': isDragOver }"
        @drop="onDrop"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @click="triggerFileInput"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".pptx,.ppt,.pdf"
          @change="onFileSelect"
          style="display: none;"
        />

        <div v-if="!isUploading" class="upload-prompt">
          <feather-icon icon="UploadIcon" size="32" class="text-primary mb-2" />
          <p class="mb-1"><strong>Click to upload</strong> or drag and drop</p>
          <p class="text-muted small">PowerPoint (.pptx, .ppt) or PDF files</p>
          <p class="text-muted small">Max size: 50MB</p>
        </div>

        <div v-else class="upload-progress">
          <feather-icon icon="UploadIcon" size="32" class="text-primary mb-2" />
          <p class="mb-2">Uploading and converting...</p>
          <b-progress :value="uploadProgress" variant="primary" class="mb-2"></b-progress>
          <p class="text-muted small">{{ uploadStatus }}</p>
        </div>
      </div>

      <!-- Current Presentation Display -->
      <div v-if="currentPresentation" class="current-presentation mt-3">
        <div class="d-flex justify-content-between align-items-center p-3 border rounded">
          <div class="d-flex align-items-center">
            <feather-icon icon="FileTextIcon" size="24" class="text-success mr-2" />
            <div>
              <div class="font-weight-bold">{{ currentPresentation.original_filename }}</div>
              <small class="text-muted">
                {{ currentPresentation.total_pages }} pages •
                Uploaded {{ formatDate(currentPresentation.created_at) }}
              </small>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <b-button
              v-if="role === 'teacher'"
              variant="outline-danger"
              size="sm"
              @click="removePresentation"
              class="ml-2"
            >
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </div>
        </div>
      </div>

      <!-- Error Display -->
      <div v-if="uploadError" class="alert alert-danger mt-3">
        <feather-icon icon="AlertCircleIcon" size="16" class="mr-1" />
        {{ uploadError }}
      </div>

      <!-- Success Display -->
      <div v-if="uploadSuccess" class="alert alert-success mt-3">
        <feather-icon icon="CheckCircleIcon" size="16" class="mr-1" />
        {{ uploadSuccess }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'

export default {
  name: 'PresentationUpload',
  props: {
    webinarId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      isDragOver: false,
      isUploading: false,
      uploadProgress: 0,
      uploadStatus: '',
      uploadError: null,
      uploadSuccess: null,
      maxFileSize: 50 * 1024 * 1024, // 50MB
      allowedExtensions: ['.pptx', '.ppt', '.pdf']
    }
  },
  computed: {
    ...mapGetters('user', ['role']),
    ...mapGetters('lessonRoom', ['currentPresentation'])
  },
  methods: {
    ...mapActions('lessonRoom', [
      'uploadPresentation',
      'removeCurrentPresentation'
    ]),

    triggerFileInput() {
      if (!this.isUploading && this.role === 'teacher') {
        this.$refs.fileInput.click()
      }
    },

    onDragOver(e) {
      e.preventDefault()
      if (this.role === 'teacher' && !this.isUploading) {
        this.isDragOver = true
      }
    },

    onDragLeave() {
      this.isDragOver = false
    },

    onDrop(e) {
      e.preventDefault()
      this.isDragOver = false

      if (this.role !== 'teacher' || this.isUploading) {
        return
      }

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        this.handleFileUpload(files[0])
      }
    },

    onFileSelect(e) {
      const files = Array.from(e.target.files)
      if (files.length > 0) {
        this.handleFileUpload(files[0])
      }
    },

    async handleFileUpload(file) {
      // Reset states
      this.uploadError = null
      this.uploadSuccess = null

      // Validate file
      const validationError = this.validateFile(file)
      if (validationError) {
        this.uploadError = validationError
        return
      }

      this.isUploading = true
      this.uploadProgress = 0
      this.uploadStatus = 'Preparing upload...'

      try {
        // Create FormData
        const formData = new FormData()
        formData.append('presentation', file)
        formData.append('webinar_id', this.webinarId)

        // Upload with progress tracking
        await this.uploadPresentation({
          formData,
          onProgress: this.onUploadProgress
        })

        this.uploadSuccess = 'Presentation uploaded and converted successfully!'
        this.uploadProgress = 100
        this.uploadStatus = 'Upload complete!'

        // Clear file input
        this.$refs.fileInput.value = ''

        // Clear success message after 3 seconds
        setTimeout(() => {
          this.uploadSuccess = null
        }, 3000)

      } catch (error) {
        console.error('Upload error:', error)
        this.uploadError = error.message || 'Failed to upload presentation'
        this.uploadProgress = 0
        this.uploadStatus = ''
      } finally {
        setTimeout(() => {
          this.isUploading = false
        }, 1000)
      }
    },

    validateFile(file) {
      // Check file extension
      const fileName = file.name.toLowerCase()
      const hasValidExtension = this.allowedExtensions.some(ext =>
        fileName.endsWith(ext)
      )

      if (!hasValidExtension) {
        return `Invalid file type. Please select a PowerPoint (.pptx, .ppt) or PDF file.`
      }

      // Check file size
      if (file.size > this.maxFileSize) {
        return `File size too large. Maximum size is ${this.formatFileSize(this.maxFileSize)}.`
      }

      return null
    },

    onUploadProgress(progress) {
      if (progress.lengthComputable) {
        this.uploadProgress = Math.round((progress.loaded / progress.total) * 90) // Reserve 10% for conversion
        this.uploadStatus = `Uploading... ${this.uploadProgress}%`
      } else {
        this.uploadStatus = 'Uploading...'
      }

      // Simulate conversion progress
      if (this.uploadProgress >= 90) {
        this.uploadStatus = 'Converting to PDF...'
      }
    },

    async removePresentation() {
      if (!confirm('Are you sure you want to remove the current presentation?')) {
        return
      }

      try {
        await this.removeCurrentPresentation(this.webinarId)
        this.uploadSuccess = 'Presentation removed successfully'

        setTimeout(() => {
          this.uploadSuccess = null
        }, 3000)
      } catch (error) {
        console.error('Remove presentation error:', error)
        this.uploadError = error.message || 'Failed to remove presentation'
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatDate(date) {
      return moment(date).format('MMM D, YYYY [at] HH:mm')
    }
  },

  mounted() {
    // Check if teacher role for upload permissions
    if (this.role !== 'teacher') {
      console.log('Presentation upload is only available for teachers')
    }
  }
}
</script>

<style lang="scss" scoped>
.presentation-upload {
  .upload-header {
    padding: 0.75rem 1rem;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #e3e3e3;

    h6 {
      color: #2c3e50;
      font-weight: 600;
    }
  }

  .upload-content {
    padding: 1rem;

    .upload-zone {
      border: 2px dashed #e3e3e3;
      border-radius: 8px;
      padding: 2rem;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
      background: #fafafa;

      &:hover {
        border-color: #007bff;
        background: #f8f9ff;
      }

      &.drag-over {
        border-color: #007bff;
        background: #e7f3ff;
        transform: scale(1.02);
      }

      .upload-prompt {
        p {
          margin-bottom: 0.25rem;
        }

        .small {
          font-size: 0.875rem;
        }
      }

      .upload-progress {
        p {
          margin-bottom: 0.5rem;
        }
      }
    }

    .current-presentation {
      .border {
        border-color: #e3e3e3 !important;
      }
    }
  }
}

// Progress bar customization
.progress {
  height: 6px;

  .progress-bar {
    transition: width 0.3s ease;
  }
}

// Alert customizations
.alert {
  padding: 0.75rem 1rem;
  border-radius: 6px;
  display: flex;
  align-items: center;

  .feather {
    flex-shrink: 0;
  }
}
</style>